import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { HelpCircle, Users, Trophy, TrendingUp, CheckCircle, Lightbulb } from "lucide-react";

export function AccountabilityGuide() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <HelpCircle className="h-4 w-4" />
          How This Works
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">Accountability & Challenges Guide</DialogTitle>
          <DialogDescription>
            Harness the power of community support to achieve your wellness goals
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Why It Works */}
          <div>
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-primary" />
              Why Accountability Works
            </h3>
            <div className="space-y-3 text-sm">
              <Card className="border-primary/20">
                <CardContent className="pt-4">
                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-primary/10 p-2">
                      <TrendingUp className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium mb-1">Significantly Better Results</p>
                      <p className="text-muted-foreground">
                        Studies show people with accountability partners have 65-95% better adherence to their goals and see lasting change.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-primary/20">
                <CardContent className="pt-4">
                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-primary/10 p-2">
                      <Users className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium mb-1">Shared Struggles, Shared Strength</p>
                      <p className="text-muted-foreground">
                        Knowing someone else understands your journey reduces isolation and increases motivation. You're not alone.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-primary/20">
                <CardContent className="pt-4">
                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-primary/10 p-2">
                      <Trophy className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium mb-1">Celebrate Wins Together</p>
                      <p className="text-muted-foreground">
                        Sharing progress amplifies the positive emotions and creates momentum. Small wins become big victories.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* How to Use */}
          <div>
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-primary" />
              How to Get Started
            </h3>
            <div className="space-y-4 text-sm">
              <div className="pl-4 border-l-2 border-primary/40">
                <div className="flex items-center gap-2 mb-1">
                  <Badge>Step 1</Badge>
                  <p className="font-medium">Find Your People</p>
                </div>
                <p className="text-muted-foreground">
                  Go to <strong>My Partners</strong> to invite someone by email, or browse the <strong>Progress Feed</strong> to connect with others on similar journeys.
                </p>
              </div>

              <div className="pl-4 border-l-2 border-primary/40">
                <div className="flex items-center gap-2 mb-1">
                  <Badge>Step 2</Badge>
                  <p className="font-medium">Join or Create a Challenge</p>
                </div>
                <p className="text-muted-foreground">
                  Visit <strong>Browse</strong> to join public challenges like "30-Day Sleep Challenge" or create your own custom challenge with specific goals.
                </p>
              </div>

              <div className="pl-4 border-l-2 border-primary/40">
                <div className="flex items-center gap-2 mb-1">
                  <Badge>Step 3</Badge>
                  <p className="font-medium">Track Your Progress</p>
                </div>
                <p className="text-muted-foreground">
                  Progress updates automatically from your daily check-ins. Watch your streak grow and see real-time stats in <strong>My Challenges</strong>.
                </p>
              </div>

              <div className="pl-4 border-l-2 border-primary/40">
                <div className="flex items-center gap-2 mb-1">
                  <Badge>Step 4</Badge>
                  <p className="font-medium">Share Your Wins</p>
                </div>
                <p className="text-muted-foreground">
                  Use the <strong>Share Progress</strong> button to celebrate milestones, completed challenges, or breakthrough moments with your accountability network.
                </p>
              </div>

              <div className="pl-4 border-l-2 border-primary/40">
                <div className="flex items-center gap-2 mb-1">
                  <Badge>Step 5</Badge>
                  <p className="font-medium">Support Others</p>
                </div>
                <p className="text-muted-foreground">
                  React to progress updates in the <strong>Progress Feed</strong>, send encouraging messages, and build meaningful connections that last.
                </p>
              </div>
            </div>
          </div>

          {/* What You Get */}
          <div>
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Trophy className="h-5 w-5 text-primary" />
              What You'll Gain
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Card className="bg-muted/50">
                <CardContent className="pt-4">
                  <p className="font-medium mb-1">✨ Increased Motivation</p>
                  <p className="text-xs text-muted-foreground">
                    Stay committed when you know someone's rooting for you
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-muted/50">
                <CardContent className="pt-4">
                  <p className="font-medium mb-1">📊 Real Progress Tracking</p>
                  <p className="text-xs text-muted-foreground">
                    Automatic updates from your daily activities
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-muted/50">
                <CardContent className="pt-4">
                  <p className="font-medium mb-1">🤝 Genuine Connection</p>
                  <p className="text-xs text-muted-foreground">
                    Build friendships with people who truly understand
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-muted/50">
                <CardContent className="pt-4">
                  <p className="font-medium mb-1">🎯 Goal Achievement</p>
                  <p className="text-xs text-muted-foreground">
                    Turn intentions into lasting habits and results
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-muted/50">
                <CardContent className="pt-4">
                  <p className="font-medium mb-1">🏆 Celebrate Wins</p>
                  <p className="text-xs text-muted-foreground">
                    Amplify joy by sharing success with your community
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-muted/50">
                <CardContent className="pt-4">
                  <p className="font-medium mb-1">💪 Consistent Support</p>
                  <p className="text-xs text-muted-foreground">
                    Get encouragement during tough days when you need it most
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="border-t pt-4">
            <p className="text-sm text-muted-foreground italic">
              💡 <strong>Pro Tip:</strong> Start small! Join one challenge and invite one accountability partner. 
              Build from there as you discover what works best for you.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
